import { useState, useEffect } from "react";
import { useSalvarConversaNoStorage } from "../hooks/SalvarConversaNoStorage";
import { usePergunta } from "../contexts/PerguntaContext";
import i18n from "i18next"; // Importe o i18n para acessar a linguagem atual
import { EmpreenderApps } from "../files/AppsArray";
import { lerConversas } from "../lib/armazenamento";
import { mensagensParaIA, perguntarIA } from "../lib/conversaIA";
import { registrarPerguntaSak } from "../lib/metricas";

interface Artigo {
  nomeartigo: string;
  link: string;
  descricao?: string;
}

// Cobre a IA (até 30 s) + o plano B na SAK.
const TEMPO_LIMITE_MS = 60000;

interface UseFetchRespostaGPTProps {
  slugAtual: string;
}

interface UseFetchRespostaGPTReturn {
  loading: boolean;
}

export const ObterRespostaGPT = ({
  slugAtual,
}: UseFetchRespostaGPTProps): UseFetchRespostaGPTReturn => {
  const [loading, setLoading] = useState<boolean>(false);

  const { pergunta, limparPergunta, conversaAtual, setErroPergunta } =
    usePergunta();
  const { salvarConversa } = useSalvarConversaNoStorage();

  const idiomaAtual = i18n.language;

  const idiomaApi =
    idiomaAtual === "pt" ? "pt-br" : idiomaAtual === "es" ? "es-mx" : "pt-br";

  useEffect(() => {
    if (!pergunta) return;

    const controller = new AbortController();

    const fetchMensagem = async () => {
      setLoading(true);
      setErroPergunta(null);

      const data = {
        pergunta,
        categorias: [],
        num_artigos: 5,
        origem: "app-suporte",
        idioma: idiomaApi, // Use o idioma da API aqui
        sessao: conversaAtual,
      };

      try {
        // 1º a IA da central (conversa com memória, SAK só como busca de artigos).
        const dadosApp = EmpreenderApps.find(
          (app) => app.slug === slugAtual || app.sluges === slugAtual,
        );
        const historico =
          lerConversas()[dadosApp?.slugamigavel || ""]?.[conversaAtual] || [];
        const mensagens = mensagensParaIA(historico, i18n.t("api_texto_print"));

        if (
          dadosApp &&
          mensagens.length &&
          mensagens[mensagens.length - 1].papel === "usuario"
        ) {
          try {
            const ia = await perguntarIA(
              {
                slug: slugAtual,
                app: idiomaAtual === "es" ? dadosApp.namees : dadosApp.name,
                idioma: idiomaAtual === "es" ? "es" : "pt",
                conversa: conversaAtual,
                mensagens,
              },
              controller.signal,
            );
            salvarConversa(
              slugAtual,
              conversaAtual,
              { assistente: ia.resposta },
              ia.artigos,
              [],
              null,
              {
                fonte: "ia",
                registro: ia.registro ?? null,
                modo: ia.modo,
                opcoes: ia.opcoes,
                artigoUsado: ia.artigo_usado?.link || null,
                artigoUsadoTitulo: ia.artigo_usado?.titulo || null,
                // Artigo completo para "Ver artigo completo" (sai primeiro se o storage encher).
                artigoHtml: ia.artigo_usado?.html || null,
                precisaHumano: ia.precisa_humano,
                resumoCaso: ia.resumo_caso,
              },
            );
            return;
          } catch (erroIA) {
            if (controller.signal.aborted) throw erroIA;
            console.warn(
              "IA da central indisponível, usando a SAK direto:",
              erroIA,
            );
          }
        }

        // 2º plano B: a SAK direto (uma pergunta por vez, sem memória).
        const response = await fetch(
          `https://app.sak.com.br/Bot/conversar/${slugAtual}/central`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`status ${response.status}`);
        }

        const resposta = await response.json();

        // Entra nas métricas também; a pergunta vai como a pessoa escreveu (sem o texto do print).
        const ultimaDoUsuario = [...historico].reverse().find((m) => m.tipo === "usuario");
        const registro = await registrarPerguntaSak({
          conversa: conversaAtual,
          app: slugAtual,
          idioma: idiomaAtual === "es" ? "es" : "pt",
          pergunta: ultimaDoUsuario?.texto || pergunta,
          resposta: String(resposta.resposta || ""),
          semArtigo: !(resposta.artigos || []).length,
        });

        // A descrição dos artigos agora é guardada: vira o resumo do cartão de cada artigo.
        salvarConversa(
          slugAtual,
          conversaAtual,
          { assistente: resposta.resposta },
          (resposta.artigos || []) as Artigo[],
          [],
          resposta.id_resposta,
          { registro },
        );
      } catch (err: any) {
        // Cancelada pela troca de pergunta/conversa: sem erro. Cancelada pelo tempo limite: erro.
        if (controller.signal.aborted && !expirou) return;
        console.error(err);
        // Guarda a pergunta para o "Tentar de novo" e libera o campo de digitação,
        // que antes ficava travado depois de um erro.
        setErroPergunta(pergunta);
      } finally {
        clearTimeout(limite);
        if (!controller.signal.aborted || expirou) {
          setLoading(false);
          limparPergunta();
        }
      }
    };

    // Sem limite, uma SAK travada deixava o "procurando…" girando para sempre.
    let expirou = false;
    const limite = setTimeout(() => {
      expirou = true;
      controller.abort();
    }, TEMPO_LIMITE_MS);

    fetchMensagem();

    return () => {
      clearTimeout(limite);
      controller.abort();
    };
  }, [slugAtual, pergunta, conversaAtual, limparPergunta, idiomaApi]);

  return { loading };
};
